/**
 * Locale plumbing: maps the app's language codes to BCP 47 tags for Intl and
 * <html lang>, and formats prices, dates and plurals.
 *
 * The app code `me` is not a valid locale ("me" is unassigned), so Intl falls
 * back to the runtime default (English plurals, "€26.99"). Every Intl call goes
 * through toIntlLocale instead.
 */
import type { Language } from '@/types';

const INTL_LOCALES: Record<Language, string> = {
  me: 'sr-Latn-ME',
  en: 'en-GB',
  sr: 'sr-Latn-RS',
  hr: 'hr-HR',
  bs: 'bs-Latn-BA',
  sl: 'sl-SI',
  mk: 'mk-MK',
  sq: 'sq-AL',
  bg: 'bg-BG',
  ro: 'ro-RO',
  el: 'el-GR',
  tr: 'tr-TR',
};

/** `<html lang>` values that differ from the app code (Q14: Montenegrin is `cnr`; Serbian is Latin). */
const HTML_LANG_OVERRIDES: Partial<Record<Language, string>> = {
  me: 'cnr',
  sr: 'sr-Latn',
};

/** Locale used only for the digits of a price; the format is the same in every language. */
const PRICE_NUMBER_LOCALE = 'sr-Latn-ME';
const NBSP = '\u00a0';

const priceNumberFormat = new Intl.NumberFormat(PRICE_NUMBER_LOCALE, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** The 12 app languages (Q1), in the order of the Language type. */
export const LANGUAGES = Object.keys(INTL_LOCALES) as readonly Language[];

/** True for one of the 12 app language codes (`?lang=` allowlist, §6). */
export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value);
}

/** BCP 47 locale for Intl APIs. */
export function toIntlLocale(language: Language): string {
  return INTL_LOCALES[language];
}

/** Value for `<html lang>`. */
export function toHtmlLang(language: Language): string {
  return HTML_LANG_OVERRIDES[language] ?? language;
}

/**
 * Price in euros, "26,99 €" in every language (style guide §4): comma, two
 * decimals, no-break space, euro sign after the amount.
 */
export function formatPrice(amount: number): string {
  return `${priceNumberFormat.format(amount)}${NBSP}€`;
}

/**
 * An approximate amount ("Oko 9 € mjesečno", Luka's WEB5): rounded to the cent, and a whole
 * euro amount drops the decimals ("9 €"); otherwise the same format as formatPrice.
 */
export function formatApproxPrice(amount: number): string {
  const cents = Math.round(amount * 100);
  return cents % 100 === 0 ? `${cents / 100}${NBSP}€` : formatPrice(cents / 100);
}

/** Date in the language's locale. Without options it is the locale's numeric date. */
export function formatDate(
  value: Date | string | number,
  language: Language,
  options?: Intl.DateTimeFormatOptions,
): string {
  const date = value instanceof Date ? value : new Date(value);
  return new Intl.DateTimeFormat(toIntlLocale(language), options).format(date);
}

/**
 * Put the formatted date into a `{date}` template. Several locales end a numeric date with a dot
 * ("29. 10. 2026."), so that dot is dropped where the template's own full stop follows.
 */
export function fillDate(template: string, value: Date | string | number, language: Language): string {
  const date = formatDate(value, language);
  return template.replace('{date}', template.includes('{date}.') ? date.replace(/\.$/, '') : date);
}

/** CLDR plural category of `count` in the language (e.g. one/few/other for Montenegrin). */
export function pluralCategory(count: number, language: Language): Intl.LDMLPluralRule {
  return new Intl.PluralRules(toIntlLocale(language)).select(count);
}

/** Forms per plural category; `other` is required and is the fallback. */
export type PluralForms = Partial<Record<Intl.LDMLPluralRule, string>> & { other: string };

/** Picks the form for `count` in the language. */
export function selectPlural(count: number, language: Language, forms: PluralForms): string {
  return forms[pluralCategory(count, language)] ?? forms.other;
}
